import reminderAsset from '../assets/SystemSettings/Reminderconfig.webp';
import serviceAsset from '../assets/SystemSettings/System Service Configuration.webp';
import rbacAsset from '../assets/SystemSettings/RBAC Configuration.webp';
import languageAsset from '../assets/SystemSettings/Language Config.webp';
import proctoringAsset from '../assets/SystemSettings/Proctoring Configuration.webp';
import interviewEmailAsset from '../assets/SystemSettings/Interview Email Configuration.webp';
import jobDivaAsset from '../assets/SystemSettings/JobDiva Integration.webp';
import interviewConfigAsset from '../assets/SystemSettings/Interview Configuration.webp';
import {
  ChevronDown,
  Lock,
  AlertTriangle,
  Check
} from 'lucide-react';
import themeAsset from '../assets/SystemSettings/Appearance.svg';
import avatarAsset from '../assets/SystemSettings/Avatar Configuration.webp';
import promptsAsset from '../assets/SystemSettings/AI Prompts Configuration.webp';
import atsAsset from '../assets/SystemSettingSand/ats-score-icon.svg';
import skillsAsset from '../assets/SystemSettings/stack.webp';
import resumeAsset from '../assets/SystemSettingSand/resume-analysis-icon.svg';
import interviewAsset from '../assets/SystemSettingSand/interview-icon.svg';
import React, { useId, useState } from 'react';
import { Select, MenuItem } from '@mui/material';
import openai from '../assets/SystemSettingSand/openai-icon.svg';
import google from '../assets/SystemSettingSand/Google-plus-logo.svg';
import anthropic from '../assets/SystemSettingSand/anthropic-icon.svg';
import meta from '../assets/SystemSettingSand/mete-icon.svg';
import mistral from '../assets/SystemSettingSand/mistral-icon.svg';
import cohere from '../assets/SystemSettingSand/cohere-icon.svg';
import groq from '../assets/SystemSettingSand/groq-icon.svg';
const providerIcons = {openai, google, gemini: google, anthropic, meta, mistral, cohere, groq};
const sectionAssets = {
 'Reminder Configurations': reminderAsset,
 'System Service Configuration': serviceAsset,
 'RBAC Configuration': rbacAsset,
 'Interview Language Config': languageAsset,
 'Interview Proctoring Configuration': proctoringAsset,
 'Interview Email Configuration': interviewEmailAsset,
 'JobDiva Integration': jobDivaAsset,
 'Interview Configuration': interviewConfigAsset,
 'Interview LLM': interviewAsset,
 'Resume Analysis LLM': resumeAsset,
 'Skill Extraction LLM': skillsAsset,
 'ATS Score LLM': atsAsset,
 'AI Prompts Configuration': promptsAsset,
 'Avatar Configuration': avatarAsset,
 'Theme Customization': themeAsset,
};



export const SettingsSection = ({ icon: Icon, title, description, children, collapsible = false, info }) => {
  const [collapsed, setCollapsed] = useState(false);
  const contentId = useId();
  return (
    <section className="settings-section" data-section={title}>
      <header className="settings-section-header">
        <span className="settings-section-icon">{sectionAssets[title] ? <img src={sectionAssets[title]} alt="" /> : <Icon aria-hidden="true" />}</span>
        <div className="settings-section-heading"><h2>{title}</h2>{description && <p>{description}</p>}</div>
        {info && <span className="settings-help" tabIndex={0} aria-label={info}>i<span role="tooltip">{info}</span></span>}
        {collapsible && <button type="button" className="settings-collapse" aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${title}`} aria-expanded={!collapsed} aria-controls={contentId} onClick={() => setCollapsed(!collapsed)}><ChevronDown style={{ transform: collapsed ? 'rotate(-90deg)' : undefined }} /></button>}
      </header>
      <div id={contentId} className="settings-section-body" hidden={collapsed}>{children}</div>
    </section>
  );
};

export const InputField = React.memo(({ label, value, onChange, type = 'text', placeholder = '', helpText = '', disabled = false, error = '', canEdit = true, leadingIcon: LeadingIcon = null }) => (
  <div className="settings-field space-y-2.5">
    <div className="flex items-center justify-between">
      <label className="block text-sm font-semibold text-[var(--text-secondary)]">{label}</label>
      {(disabled || !canEdit) && <Lock className="w-4 h-4 text-gray-400" />}
    </div>
    <div className="relative">
      {LeadingIcon && <LeadingIcon className="settings-input-leading-icon" aria-hidden="true" />}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled || !canEdit}
        className={`w-full px-4 py-3 bg-[var(--bg-input)] border rounded-xl focus:border-transparent outline-none transition-all hover:border-[var(--primary)] disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 shadow-sm hover:shadow-md text-[var(--text-primary)] ${LeadingIcon ? 'settings-input-has-leading-icon' : ''} ${
          error
            ? 'border-red-500 focus:ring-2 focus:ring-red-600'
            : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--primary)]'
        }`}
      />
    </div>
    {error && <p className="text-xs text-red-600 font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {error}</p>}
    {helpText && !error && <p className="text-xs text-[var(--text-muted)] font-medium">{helpText}</p>}
  </div>
));

export const SelectField = React.memo(({ label, value, onChange, options, helpText = '', canEdit = true }) => {
  const labelId = useId();
  const visibleOptions = options.some(opt => String(opt.value) === String(value)) ? options : [{ value: value || '', label: value || 'Select an option' }, ...options];
  return (
    <div className="settings-field">
      <div className="flex items-center justify-between"><label id={labelId}>{label}</label>{!canEdit && <Lock className="w-4 h-4 text-gray-400" />}</div>
      <Select labelId={labelId} value={value ?? ''} onChange={onChange} disabled={!canEdit} displayEmpty fullWidth size="small" className="settings-select"
        renderValue={selected => visibleOptions.find(opt => String(opt.value) === String(selected))?.label || selected || 'Select an option'}
        MenuProps={{ PaperProps: { className: 'settings-select-menu' } }}>
        {visibleOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>
          {opt.icon && <span className="settings-dropdown-icon" aria-hidden="true">{opt.icon}</span>}
          {(label === 'Provider' || label === 'Select Provider') && providerIcons[String(opt.value).toLowerCase()] && <img className="settings-provider-logo" src={providerIcons[String(opt.value).toLowerCase()]} alt="" />}
          <span>{opt.label}</span>{String(value) === String(opt.value) && <Check className="settings-option-check" size={16} />}
        </MenuItem>)}
      </Select>
      {helpText && <p className="settings-field-help">{helpText}</p>}
    </div>
  );
});

export const ToggleSwitch = React.memo(({ label, checked, onChange, description = '', canEdit = true }) => (
  <div className={`settings-toggle flex items-center justify-between p-5 border border-[var(--border-color)] rounded-xl transition-colors shadow-sm ${!canEdit ? 'bg-gray-50' : 'hover:bg-[var(--primary-light)]'}`}>
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <label className={`block text-sm font-semibold ${!canEdit ? 'text-gray-500' : 'text-[var(--text-secondary)]'}`}>{label}</label>
        {!canEdit && <Lock className="w-4 h-4 text-gray-400" />}
      </div>
      {description && <p className={`text-sm mt-2 font-medium ${!canEdit ? 'text-gray-400' : 'text-[var(--text-muted)]'}`}>{description}</p>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => !canEdit || onChange(!checked)}
      disabled={!canEdit}
      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-all duration-300 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? 'bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] shadow-lg shadow-[var(--primary)]/30' : 'bg-[var(--border-color)]'
      }`}
    >
      <span
        className={`h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-300 flex items-center justify-center ${
          checked ? 'translate-x-7' : 'translate-x-1'
        }`}
      >
        {checked && <Check className="w-3.5 h-3.5 text-[var(--primary-dark)]" />}
      </span>
    </button>
  </div>
));

export const SliderField = React.memo(({ label, value, onChange, min = 0, max = 100, step = 1, helpText = '', canEdit = true }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <label className="block text-sm font-semibold text-[var(--text-secondary)]">{label}</label>
      <span className="px-3 py-1 bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white text-sm font-bold rounded-full shadow-md">{value}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={!canEdit}
      className="w-full h-3 bg-[var(--border-color)] rounded-full appearance-none cursor-pointer accent-[var(--primary)] hover:accent-[var(--primary-hover)] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
    />
    {helpText && <p className="text-xs text-[var(--text-muted)] font-medium">{helpText}</p>}
  </div>
));
